import React, {
  FormEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useApi } from '../state/ApiContext';
import AsyncButton from '../components/AsyncButton';
import { useGuardedCallback } from '../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../permissions/permission-catalog';
import { formatCostTotal } from '../utils/formatCost';

export interface CreditRedemption {
  amountUsd: number;
  balanceUsd: number;
  fundingEntryId: number;
}

interface CreditCodeRedemptionFormProps {
  teamId: number;
  onRedeemed?: (redemption: CreditRedemption) => void;
  onBusyChange?: (busy: boolean) => void;
}

interface ApiFailure {
  response?: {
    status?: number;
    data?: {
      error?: unknown;
      retryAfterSeconds?: unknown;
    };
  };
}

const CreditCodeRedemptionForm: React.FC<CreditCodeRedemptionFormProps> = ({
  teamId,
  onRedeemed,
  onBusyChange,
}) => {
  const { billingApi } = useApi();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);
  const instanceId = useId();
  const inputId = `credit-code-input-${instanceId}`;
  const helpId = `credit-code-help-${instanceId}`;

  useLayoutEffect(() => {
    requestGenerationRef.current += 1;
    onBusyChange?.(false);
    setCode('');
    setBusy(false);
    setError(null);
    setSuccess(null);
    return () => {
      requestGenerationRef.current += 1;
      onBusyChange?.(false);
    };
  }, [billingApi, onBusyChange, teamId]);

  /** Every write here is `billing: manage` — it moves money. */
  const billingGuard = useGuardedCallback<[action: () => void]>({
    resource: PermissionResource.BILLING,
    level: PermissionLevel.MANAGE,
    verb: 'change',
    subject: "the team's billing",
    testId: 'billing-denied',
    onAllowed: (action) => action(),
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || busy) return;

    const requestGeneration = ++requestGenerationRef.current;
    setBusy(true);
    onBusyChange?.(true);
    setError(null);
    setSuccess(null);
    try {
      const response =
        await billingApi.apiTeamTeamIdBillingCreditCodesRedeemPost(teamId, {
          code,
        });
      if (requestGenerationRef.current !== requestGeneration) return;
      const redemption = response.data;
      setCode('');
      setSuccess(
        `${formatCostTotal(redemption.amountUsd)} in credit added to your team. New balance: ${formatCostTotal(redemption.balanceUsd)}`,
      );
      onRedeemed?.(redemption);
    } catch (caught) {
      if (requestGenerationRef.current !== requestGeneration) return;
      const failure = caught as ApiFailure;
      const { status, data } = failure.response || {};
      if (status === 429) {
        const retryAfterSeconds = Number(data?.retryAfterSeconds);
        const minutes = Math.max(
          1,
          Math.ceil(
            (Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 0) / 60,
          ),
        );
        setError(
          `Too many attempts. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
        );
      } else {
        setError(
          typeof data?.error === 'string'
            ? data.error
            : 'Failed to redeem credit code',
        );
      }
    } finally {
      if (requestGenerationRef.current === requestGeneration) {
        setBusy(false);
        onBusyChange?.(false);
      }
    }
  };

  return (
    <form
      className="billing-card-form"
      onSubmit={(event) => {
        event.preventDefault();
        billingGuard.run(() => void submit(event));
      }}>
      <label className="add-funds-amount-label" htmlFor={inputId}>
        Credit code
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          disabled={busy}
          data-testid="credit-code-input"
          aria-describedby={helpId}
          onChange={(event) => setCode(event.target.value)}
        />
      </label>
      <p id={helpId} className="descriptionText">
        Enter the single-use code exactly as it appears.
      </p>
      {error && (
        <div
          className="projects-error"
          role="alert"
          data-testid="credit-code-error">
          {error}
        </div>
      )}
      {success && (
        <div
          className="success-message"
          role="status"
          data-testid="credit-code-success">
          {success}
        </div>
      )}
      <div className="billing-card-form-actions">
        <AsyncButton
          className="primaryButton"
          type="submit"
          loading={busy}
          loadingText="Processing..."
          disabled={busy || !code.trim()}
          data-testid="credit-code-submit">
          Redeem credit
        </AsyncButton>
      </div>
      {billingGuard.denialModal}
    </form>
  );
};

export default CreditCodeRedemptionForm;
